/**
 * অবস্থা ৯ · সেবাটি আসছে — `P0 States § ১১` (Not yet live · ত্রুটি ০২).
 * `E_DOMAIN_NOT_LIVE`.
 *
 * The eleventh state, added by the audit. Frame note: "The most frequent state of the
 * first months, and it was missing: search into an unlaunched domain. Two things that
 * do work, a notify action, and the search logged as demand — so the state feeds the
 * roadmap instead of just apologising."
 *
 * USEFUL, NOT APOLOGETIC — AND NOW TRUE.
 *
 * docs/05: "`E_DOMAIN_NOT_LIVE` … must include `alternatives[]` and
 * `notify_available: true`, because the state's whole job is to be useful rather than
 * apologetic." This component used to guarantee that by TYPE — a non-empty tuple of
 * alternatives was required — and the boundary satisfied the type with the two drawn
 * ones: "buy medicine" and "the ambulance number", under a heading about health, beneath
 * a footer saying five domains were live and seven coming. A customer whose search hit
 * any unlaunched domain was told it was health, offered medicine, and given the domain
 * count of a product that no longer exists (all twelve are live, founder decision
 * 2026-09-18). The guarantee held; the content was invented.
 *
 * W1-04 moves the guarantee to where it can be kept honestly:
 *
 *   · the heading is the server's `title_bn`, or the state's own drawn name;
 *   · the body is `message_bn`, or nothing;
 *   · alternatives are the ones the server SENT (`DomainNotLiveError.alternatives`), or
 *     none — the heading over them is drawn only when there are some;
 *   · the notify control is drawn only when the server says notify is available AND
 *     something records the request (`onNotify`). A "tell me when it opens" that tells
 *     nobody is the one control on this screen that would be a lie;
 *   · and there is always a way on: in the notify control's place, the search door.
 *
 * The demand line ("your search was added to the demand list") and the coverage footer
 * are gone: the first is a claim about a database row this component cannot see, the
 * second a count that is no longer true. `routes/search` renders its own § ১১ from the
 * search answer, which does carry both, and is unaffected. `docs/deviations/W1-04.md` (S11).
 */
import { ariaLabel } from '@oprappo/tokens';
import type { ApiErrorBody } from '@oprappo/contracts';
import { StateFrame, ChevronRow, SectionLabel, BackChevron } from './chrome.js';
import { DOMAIN_NOT_LIVE, SEARCH_ACTION_BN } from './data.js';
import { STATE_LABEL_BN } from './keys.js';

export interface DomainAlternative {
  id: string;
  label_bn: string;
  meta_bn?: string | undefined;
}

export interface DomainNotLiveStateProps {
  /** What the server offered instead. Empty or absent: no alternatives are drawn. */
  alternatives?: readonly DomainAlternative[] | undefined;
  /** `notify_available` from the envelope. The control also needs `onNotify`. */
  notifyAvailable?: boolean | undefined;
  /** The user's own query, echoed in the field. Never composed. */
  queryBn?: string | undefined;
  /** `title_bn` from the envelope. Absent: the state's drawn name. */
  titleBn?: string | undefined;
  /** `error.message_bn`. Absent or empty: not drawn. */
  bodyBn?: string | undefined;
  onNotify?: (() => void) | undefined;
  onAlternative?: ((id: string) => void) | undefined;
  onSearch?: (() => void) | undefined;
  onBack?: (() => void) | undefined;
}

function said(value: string | undefined): value is string {
  return value !== undefined && value.trim() !== '';
}

/**
 * The alternatives an `E_DOMAIN_NOT_LIVE` envelope actually carries, in the shape this
 * frame draws — `packages/contracts § DomainNotLiveError`: `{ offering_id, title_bn }`.
 * Anything malformed is dropped rather than drawn half-filled; an envelope with none, or
 * a different code, gives none.
 */
export function alternativesFromError(body: ApiErrorBody | undefined): DomainAlternative[] {
  if (body === undefined || body.error.code !== 'E_DOMAIN_NOT_LIVE') return [];
  const raw = (body as { alternatives?: unknown }).alternatives;
  if (!Array.isArray(raw)) return [];
  const out: DomainAlternative[] = [];
  for (const item of raw as unknown[]) {
    if (typeof item !== 'object' || item === null) continue;
    const { offering_id: id, title_bn: label } = item as { offering_id?: unknown; title_bn?: unknown };
    if (typeof id === 'string' && id !== '' && typeof label === 'string' && label.trim() !== '') {
      out.push({ id, label_bn: label });
    }
  }
  return out;
}

/** `notify_available: true` on the envelope, and nothing else counts. */
export function notifyAvailableFromError(body: ApiErrorBody | undefined): boolean {
  return (body as { notify_available?: unknown } | undefined)?.notify_available === true;
}

export function DomainNotLiveState({
  alternatives = [],
  notifyAvailable = false,
  queryBn,
  titleBn,
  bodyBn,
  onNotify,
  onAlternative,
  onSearch,
  onBack,
}: DomainNotLiveStateProps): JSX.Element {
  const d = DOMAIN_NOT_LIVE;
  const canNotify = notifyAvailable && onNotify !== undefined;
  return (
    <StateFrame stateKey="domain_not_live">
      <div className="px-[16px] pt-[16px]">
        <div className="flex gap-[12px] items-center mb-[16px]">
          <button
            type="button"
            aria-label={ariaLabel.back}
            onClick={onBack}
            className="bg-transparent border-0 p-0 rounded-none flex-none"
          >
            <BackChevron />
          </button>
          <div className="flex-1 box-content border border-border bg-white h-[46px] flex items-center px-[13px] font-anek font-medium text-[16px] leading-[1.75] text-ink">
            {queryBn ?? ''}
          </div>
        </div>
      </div>

      <div className="flex-1 px-[16px] oprappo-scroll">
        <div className="bg-white border border-border border-l-[3px] border-l-amber-text p-[15px]">
          <div className="font-anek font-semibold text-[18px] leading-[1.75] text-ink">
            {said(titleBn) ? titleBn : STATE_LABEL_BN.domain_not_live}
          </div>
          {/* Says it will not send you to the wrong place. No apology, no "Oops!". */}
          {said(bodyBn) ? (
            <div className="font-anek font-normal text-[14.5px] leading-[1.75] text-ink2 mt-[8px]">{bodyBn}</div>
          ) : null}
          <div className="flex gap-[9px] mt-[14px]">
            <button
              type="button"
              onClick={canNotify ? onNotify : onSearch}
              className="bg-teal px-[16px] py-[12px] font-anek font-semibold text-[14.5px] leading-[1.75] text-white border-0 rounded-none"
            >
              {canNotify ? d.notify_bn : SEARCH_ACTION_BN}
            </button>
          </div>
        </div>

        {/* Things that DO work, when the server named some. */}
        {alternatives.length === 0 ? null : (
          <>
            <SectionLabel className="mt-[20px] mb-[11px]">{d.alternatives_label_bn}</SectionLabel>
            <div className="flex flex-col gap-[10px]">
              {alternatives.map((a) => (
                <ChevronRow
                  key={a.id}
                  titleBn={a.label_bn}
                  metaBn={a.meta_bn}
                  onClick={onAlternative === undefined ? undefined : () => onAlternative(a.id)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </StateFrame>
  );
}
